import Papa from "papaparse";
import dayjs from "dayjs";
import { formatSecondsToTime } from "../../utils/formatDecimalNumber";
import { getCallStatusLabel } from "../../constants/callStatus";
import { formatReportChannel, getCallDurationSeconds } from "./helpers";
import {
  formatActionTitle,
  formatActionValueForCsv,
  formatCallSummaryForCsv,
  resolveCallActionKeys,
  type ReportActionKey,
} from "./reportActions";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Serialize report rows as RFC 4180 CSV.
 * Inbound Need Review notes are joined with newlines; those fields must be
 * quoted so later columns (Duration, Summary, goal status) stay aligned.
 */
export const buildCsvContent = (rows: unknown[][]): string =>
  Papa.unparse(rows, { newline: "\n" });

const dash = (value: unknown) => {
  const text = String(value ?? "").trim();
  return text || "-";
};

const goalAchievedLabel = (rawStatus: unknown, t: Translate) => {
  if (!rawStatus) return "-";
  const key = String(rawStatus);
  const knownKeys = ["achieved", "partially_achieved", "not_achieved"];
  const lookupKey = knownKeys.includes(key) ? key : "unknown";
  const translated = t(`reports.summaryStatus.${lookupKey}`);
  if (
    typeof translated === "string" &&
    translated !== `reports.summaryStatus.${lookupKey}`
  ) {
    return translated;
  }
  return key;
};

const isUnsubscribed = (row: any) =>
  String(row?.call_analysis?.unsubscribe || "")
    .trim()
    .toUpperCase() === "YES";

export const buildCallCsvTable = (
  rows: any[],
  t: Translate,
  extraActionKeys?: ReportActionKey[]
): unknown[][] => {
  const actionKeys = resolveCallActionKeys(rows, extraActionKeys);
  const includeUnsubscribe = (rows || []).some(isUnsubscribed);
  const headers = [
    t("reports.tableColumns.recipientName"),
    t("reports.tableColumns.phoneNumber"),
    t("reports.tableColumns.duration"),
    t("reports.tableColumns.status") || t("status"),
    t("reports.tableColumns.callTime"),
    ...actionKeys.map((item) => formatActionTitle(item.key)),
    t("reports.tableColumns.summary"),
    ...(includeUnsubscribe ? ["Unsubscribe"] : []),
    t("reports.tableColumns.goalAchievedStatus") || "Goal achieved status",
  ];

  const dataRows = rows.map((row: any) => {
    const values: unknown[] = [
      dash(row.recipient_name),
      dash(row.phone),
      formatSecondsToTime(getCallDurationSeconds(row)),
      getCallStatusLabel(row.status),
      row.start_time ? dayjs(row.start_time).format("hh:mm A, MMM DD, YYYY") : "-",
      ...actionKeys.map((item) =>
        formatActionValueForCsv(row.action?.[item.key])
      ),
      formatCallSummaryForCsv(row),
    ];
    if (includeUnsubscribe) {
      values.push(isUnsubscribed(row) ? "YES" : "-");
    }
    values.push(goalAchievedLabel(row.call_analysis?.goal_achieved, t));
    return values;
  });

  return [headers, ...dataRows];
};

export const buildConversationCsvTable = (
  rows: any[],
  t: Translate
): unknown[][] => {
  const headers = [
    t("reports.tableColumns.recipientName"),
    t("reports.tableColumns.phoneNumber"),
    t("reports.v2.channel"),
    t("reports.tableColumns.summary"),
  ];

  const dataRows = rows.map((row: any) => {
    return [
      dash(row.recipient_name),
      dash(row.phone),
      dash(formatReportChannel(row)),
      dash(row.summary),
    ];
  });

  return [headers, ...dataRows];
};
